import React, {
  useEffect,
  useMemo,
} from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import Controller from 'core/components/FormController';
import Select from 'core/components/Select';
import {
  validateEmail,
} from 'pages/Dashboard/utils/helper';
import Input from 'pages/Dashboard/components/Input';
import {
  AppointmentPaymentMethodDTO,
} from 'dtos/appointmentPaymentMethodDTO';
import compact from 'lodash/compact';
import {
  SquareTerminalPaymentMethodDTO,
} from 'dtos/squareTerminalPaymentMethodDTO';
import {
  useGetPaymentMethods,
} from 'views/EMR/hooks/billing';
import {
  isCheckLikePaymentMethod,
} from 'views/EMR/Billing/utils';

export type TerminalPaymentOption = {
  label: string;
  value: string;
};

export const terminalPaymentOptions: TerminalPaymentOption[] = [{
  label: 'Pay with a card',
  value: 'yes',
}, {
  label: 'Enter card details',
  value: 'no',
}];

function PaymentAdditionalFields() {
  const { watch, control } = useFormContext();
  const paymentMethod = watch('paymentMethod');
  const { discriminator } = paymentMethod ?? {};

  const isTerminal = !isNil(paymentMethod?.deviceId);
  const isCheck = isCheckLikePaymentMethod(discriminator);
  const isOnline = /^online/i.test(discriminator);
  const isCard = /^card/i.test(discriminator) && !isTerminal;

  return (
    <>
      {(isCheck || isCard) && (
        <Controller
          name="transactionId"
          rules={{
            required: isCheck
              ? 'Check number is required.'
              : 'Transaction ID is required.',
          }}
          control={control}
          render={
             ({ field, fieldState: { invalid } }) => (
               <Input
                 {...field}
                 required
                 error={invalid}
                 showErrorText={false}
                 className="w-1/2"
                 classes={{ container: '!h-8' }}
                 label={isCheck ? 'Check Number' : 'Transaction ID'}
                 placeholder={isCheck ? 'Check number' : 'Transaction ID'}
                 id="transaction-id"
               />
             )
           }
        />
      )}
      {isTerminal && (
        <Controller
          name="isCardPresent"
          rules={{ required: 'Payment type is required.' }}
          control={control}
          render={
            ({ field, fieldState: { invalid } }) => (
              <Select<TerminalPaymentOption>
                {...field}
                selectClassName="!h-8"
                className="!w-1/2"
                id="payment-method"
                label="Payment type"
                placeholder="Select"
                error={invalid}
                showErrorText={false}
                required
                options={terminalPaymentOptions}
                getValue={(option: TerminalPaymentOption) => option.value}
                getLabel={(option: TerminalPaymentOption) => option.label}
              />
            )
          }
        />
      )}
      {isOnline && (
        <Controller
          control={control}
          name="email"
          rules={{ required: 'Email is required.', validate: validateEmail }}
          render={({ field, fieldState: { invalid } }) => (
            <Input
              {...field}
              required
              id="email"
              className="w-1/2"
              label="Email"
              error={invalid}
              showErrorText={false}
              placeholder="Email"
            />
          )}
        />
      )}
    </>
  );
}

type Props = {
  allowOnline?: boolean;
};

export default function PaymentFields({ allowOnline = true }: Props) {
  const { control, watch, setValue } = useFormContext();
  const paymentMethod = watch('paymentMethod');

  const { data: paymentMethods } = useGetPaymentMethods();

  useEffect(() => {
    const hasTransactionId = /^card/i.test(paymentMethod?.discriminator ?? '')
      || isCheckLikePaymentMethod(paymentMethod?.discriminator);
    const resetTransactionId = !isNil(paymentMethod) && !hasTransactionId;

    if (resetTransactionId) {
      setValue('transactionId', null);
    }

    setValue('isCardPresent', undefined);
  }, [paymentMethod]);

  const paymentOptions = useMemo(() => ([
    ...(paymentMethods ?? []),
    ...(allowOnline ? [{
      discriminator: 'OnlinePayment',
      name: 'Online',
    }] : []),
  ]), [paymentMethods, allowOnline]);

  return (
    <div className="flex w-full gap-form-field">
      <Controller
        control={control}
        name="paymentMethod"
        rules={{ required: 'Payment method is required.' }}
        render={({ field, fieldState: { invalid } }) => (
          <Select<AppointmentPaymentMethodDTO>
            {...field}
            getValue={(option) => compact([option?.discriminator, option?.name]).join(' - ')}
            getLabel={(option) => option.name}
            id="payment-method"
            label="Payment method"
            required
            placeholder="Select"
            showErrorText={false}
            options={paymentOptions ?? []}
            getDisableStatus={(option) => !(
              (option as SquareTerminalPaymentMethodDTO)?.isAvailable ?? true)}
            error={invalid}
            className="w-1/2"
          />
        )}
      />
      <PaymentAdditionalFields />
    </div>
  );
}
